import { createLogger } from "../utils/logger";
import type {
  App,
  Install,
  User,
  Invitation,
  AuditLogEntry,
  WebhookStatus,
  WebhookFixResult,
  CreateAppInput,
  UpdateAppInput,
  UserRole,
  ThemePreference,
  AppsViewFilter,
  UserPreferences,
} from "../types";

// Re-export types for consumers
export type {
  App,
  Install,
  User,
  Invitation,
  AuditLogEntry,
  WebhookStatus,
  WebhookFixResult,
  CreateAppInput,
  UpdateAppInput,
  UserRole,
  ThemePreference,
  AppsViewFilter,
  UserPreferences,
};

const log = createLogger({ module: "api" });
const API_BASE = "/api";

// CSRF token management
let csrfToken: string | null = null;

/**
 * Extract request ID from response headers for correlation
 */
function getRequestId(res: Response): string | null {
  return res.headers.get("X-Request-ID");
}

/**
 * Enhanced fetch wrapper with logging and error handling
 */
async function apiFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const method = options.method || "GET";
  log.debug(`${method} ${url}`, { method, url });

  const res = await fetch(url, {
    ...options,
    credentials: "include",
  });

  const requestId = getRequestId(res);

  if (!res.ok) {
    log.warn(`API request failed: ${method} ${url}`, {
      status: res.status,
      requestId,
    });
  } else {
    log.debug(`API request succeeded: ${method} ${url}`, {
      status: res.status,
      requestId,
    });
  }

  return res;
}

async function getCsrfToken(): Promise<string> {
  if (csrfToken) return csrfToken;

  const res = await apiFetch(`${API_BASE}/csrf-token`);
  if (!res.ok) throw new Error("Failed to fetch CSRF token");
  const data = await res.json();
  const token = data.csrfToken as string;
  csrfToken = token;
  return token;
}

// Helper for mutation requests with CSRF
async function mutationFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const token = await getCsrfToken();
  return apiFetch(url, {
    ...options,
    headers: {
      ...options.headers,
      "x-csrf-token": token,
    },
  });
}

// Apps
export async function getApps(): Promise<App[]> {
  const res = await apiFetch(`${API_BASE}/apps`);
  if (!res.ok) throw new Error("Failed to fetch apps");
  return res.json();
}

export async function getApp(id: string): Promise<App> {
  const res = await apiFetch(`${API_BASE}/apps/${id}`);
  if (!res.ok) throw new Error("Failed to fetch app");
  return res.json();
}

export async function createApp(data: CreateAppInput): Promise<App> {
  const res = await mutationFetch(`${API_BASE}/apps`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || "Failed to create app");
  }
  return res.json();
}

export async function updateApp(id: string, data: UpdateAppInput): Promise<App> {
  const res = await mutationFetch(`${API_BASE}/apps/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || "Failed to update app");
  }
  return res.json();
}

export async function deleteApp(id: string): Promise<void> {
  const res = await mutationFetch(`${API_BASE}/apps/${id}`, {
    method: "DELETE",
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || "Failed to delete app");
  }
}

export async function getInstallUrl(appId: string, shop: string): Promise<{ url: string }> {
  const res = await mutationFetch(`${API_BASE}/apps/${appId}/install-url`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ shop }),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || "Failed to generate install URL");
  }
  return res.json();
}

// Installs
export async function getAppInstalls(appId: string): Promise<Install[]> {
  const res = await apiFetch(`${API_BASE}/apps/${appId}/installs`);
  if (!res.ok) throw new Error("Failed to fetch installs");
  return res.json();
}

// Tokens
export async function revokeToken(shop: string, token: string): Promise<void> {
  const res = await mutationFetch(`${API_BASE}/tokens/revoke`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ shop, token }),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || "Failed to revoke token");
  }
}

// Uninstall
export async function uninstallFromShop(appId: string, installId: string): Promise<void> {
  const res = await mutationFetch(`${API_BASE}/apps/${appId}/installs/${installId}/uninstall`, {
    method: "POST",
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || "Failed to uninstall app");
  }
}

// Webhook Status
export async function getWebhookStatus(appId: string, installId: string): Promise<WebhookStatus> {
  const res = await apiFetch(`${API_BASE}/apps/${appId}/installs/${installId}/webhook`);
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || "Failed to check webhook status");
  }
  return res.json();
}

export async function fixWebhook(appId: string, installId: string): Promise<WebhookFixResult> {
  const res = await mutationFetch(`${API_BASE}/apps/${appId}/installs/${installId}/webhook`, {
    method: "POST",
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || "Failed to fix webhook");
  }
  return res.json();
}

// Users
export async function getUsers(): Promise<User[]> {
  const res = await apiFetch(`${API_BASE}/users`);
  if (!res.ok) throw new Error("Failed to fetch users");
  return res.json();
}

export async function updateUserRole(id: string, role: UserRole): Promise<User> {
  const res = await mutationFetch(`${API_BASE}/users/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ role }),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || "Failed to update user role");
  }
  return res.json();
}

export async function deleteUser(id: string): Promise<void> {
  const res = await mutationFetch(`${API_BASE}/users/${id}`, {
    method: "DELETE",
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || "Failed to delete user");
  }
}

// Invitations
export async function getInvitations(): Promise<Invitation[]> {
  const res = await apiFetch(`${API_BASE}/invitations`);
  if (!res.ok) throw new Error("Failed to fetch invitations");
  return res.json();
}

export async function createInvitation(email: string, role: UserRole): Promise<Invitation> {
  const res = await mutationFetch(`${API_BASE}/invitations`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, role }),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || "Failed to create invitation");
  }
  return res.json();
}

export async function cancelInvitation(id: string): Promise<void> {
  const res = await mutationFetch(`${API_BASE}/invitations/${id}`, {
    method: "DELETE",
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || "Failed to cancel invitation");
  }
}

export async function resendInvitation(id: string): Promise<void> {
  const res = await mutationFetch(`${API_BASE}/invitations/${id}/resend`, {
    method: "POST",
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || "Failed to resend invitation");
  }
}

// Audit Log
export async function getAuditLog(options?: {
  userId?: string;
  resourceType?: string;
  resourceId?: string;
  limit?: number;
  offset?: number;
}): Promise<AuditLogEntry[]> {
  const params = new URLSearchParams();
  if (options?.userId) params.set("userId", options.userId);
  if (options?.resourceType) params.set("resourceType", options.resourceType);
  if (options?.resourceId) params.set("resourceId", options.resourceId);
  if (options?.limit) params.set("limit", options.limit.toString());
  if (options?.offset) params.set("offset", options.offset.toString());

  const queryString = params.toString();
  const url = queryString ? `${API_BASE}/audit?${queryString}` : `${API_BASE}/audit`;

  const res = await apiFetch(url);
  if (!res.ok) throw new Error("Failed to fetch audit log");
  return res.json();
}

export async function getAuditEntry(id: string): Promise<AuditLogEntry> {
  const res = await apiFetch(`${API_BASE}/audit/${id}`);
  if (!res.ok) {
    if (res.status === 404) {
      throw new Error("Audit entry not found");
    }
    throw new Error("Failed to fetch audit entry");
  }
  return res.json();
}

// User Preferences
export async function updateUserPreferences(preferences: {
  theme?: ThemePreference;
  appsViewFilter?: AppsViewFilter;
}): Promise<{ preferences: UserPreferences }> {
  const res = await mutationFetch(`${API_BASE}/auth/preferences`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(preferences),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || "Failed to update preferences");
  }
  return res.json();
}
